// owner: ads-domain agent — zod schemas + inferred types shared by api and web
import { z } from 'zod';
import { CentsSchema, EpochMsSchema, UlidSchema, UrlSchema } from './common';

export const CampaignObjectiveSchema = z.enum([
  'awareness',
  'traffic',
  'applicants',
  'followers',
  'engagement',
]);
export const BidTypeSchema = z.enum(['cpc', 'cpm']);
export const CampaignStatusSchema = z.enum([
  'draft',
  'pending_approval',
  'active',
  'paused',
  'exhausted',
  'ended',
]);
export const PlacementSchema = z.enum(['feed', 'jobs', 'profile', 'search']);

/** Empty/absent dimension = matches everyone. Values compared case-insensitively. */
export const TargetingSchema = z.object({
  skills: z.array(z.string().trim().min(1).max(60)).max(30).default([]),
  locations: z.array(z.string().trim().min(1).max(80)).max(30).default([]),
  personas: z.array(z.string().trim().min(1).max(40)).max(10).default([]),
});
export type Targeting = z.infer<typeof TargetingSchema>;

export const CampaignCreateSchema = z
  .object({
    orgId: UlidSchema,
    name: z.string().trim().min(1).max(120),
    objective: CampaignObjectiveSchema,
    budgetCents: CentsSchema.max(100_000_000),
    dailyCapCents: CentsSchema.max(100_000_000),
    bidCents: CentsSchema.min(1).max(100_000),
    bidType: BidTypeSchema.default('cpc'),
    targeting: TargetingSchema.default({ skills: [], locations: [], personas: [] }),
    startsAt: EpochMsSchema.nullish(),
    endsAt: EpochMsSchema.nullish(),
  })
  .refine((v) => v.dailyCapCents <= v.budgetCents, {
    message: 'dailyCapCents cannot exceed budgetCents',
    path: ['dailyCapCents'],
  })
  .refine((v) => v.startsAt == null || v.endsAt == null || v.endsAt > v.startsAt, {
    message: 'endsAt must be after startsAt',
    path: ['endsAt'],
  });
export type CampaignCreate = z.infer<typeof CampaignCreateSchema>;

export const CampaignPatchSchema = z
  .object({
    name: z.string().trim().min(1).max(120),
    budgetCents: CentsSchema.max(100_000_000),
    dailyCapCents: CentsSchema.max(100_000_000),
    bidCents: CentsSchema.min(1).max(100_000),
    targeting: TargetingSchema,
    startsAt: EpochMsSchema.nullable(),
    endsAt: EpochMsSchema.nullable(),
    approvalId: UlidSchema,
  })
  .partial()
  .refine((v) => Object.keys(v).length > 0, { message: 'Provide at least one field to change' });
export type CampaignPatch = z.infer<typeof CampaignPatchSchema>;

export const CampaignSchema = z.object({
  id: UlidSchema,
  orgId: UlidSchema,
  name: z.string(),
  objective: CampaignObjectiveSchema,
  status: CampaignStatusSchema,
  budgetCents: z.number().int(),
  dailyCapCents: z.number().int(),
  spentCents: z.number().int(),
  remainingCents: z.number().int(),
  bidCents: z.number().int(),
  bidType: BidTypeSchema,
  targeting: TargetingSchema,
  pacingMultiplier: z.number(),
  startsAt: z.number().int().nullable(),
  endsAt: z.number().int().nullable(),
  createdAt: z.number().int(),
  updatedAt: z.number().int(),
});
export type Campaign = z.infer<typeof CampaignSchema>;

export const CampaignListQuerySchema = z.object({
  orgId: UlidSchema,
  status: CampaignStatusSchema.optional(),
});
export const CampaignListSchema = z.object({ items: z.array(CampaignSchema) });

export const CampaignIdParamsSchema = z.object({ id: UlidSchema });
export const CampaignActivateBodySchema = z.object({ approvalId: UlidSchema.optional() });

export const CreativeCreateSchema = z.object({
  headline: z.string().trim().min(1).max(90),
  body: z.string().trim().max(300).default(''),
  imageKey: z.string().max(300).nullish(),
  cta: z.string().trim().min(1).max(40).default('learn_more'),
  targetUrl: UrlSchema.max(2048),
});
export const CreativePatchSchema = z.object({ status: z.enum(['active', 'paused']) });
export const CreativeSchema = z.object({
  id: UlidSchema,
  campaignId: UlidSchema,
  headline: z.string(),
  body: z.string(),
  imageKey: z.string().nullable(),
  cta: z.string(),
  targetUrl: z.string(),
  status: z.enum(['active', 'paused', 'rejected']),
  createdAt: z.number().int(),
});
export const CreativeListSchema = z.object({ items: z.array(CreativeSchema) });
export const CreativeIdParamsSchema = z.object({ id: UlidSchema });

export const ServeQuerySchema = z.object({
  placement: PlacementSchema.default('feed'),
  skills: z
    .string()
    .max(500)
    .optional()
    .transform((s) =>
      s
        ? s
            .split(',')
            .map((x) => x.trim())
            .filter(Boolean)
            .slice(0, 30)
        : [],
    ),
  location: z.string().max(120).optional(),
  persona: z.string().max(40).optional(),
});
export const ServedAdSchema = z.object({
  impressionId: UlidSchema,
  campaignId: UlidSchema,
  creativeId: UlidSchema,
  headline: z.string(),
  body: z.string(),
  cta: z.string(),
  imageKey: z.string().nullable(),
  clickUrl: z.string(),
  sponsored: z.literal(true),
});
export const ServeResponseSchema = z.object({
  ad: ServedAdSchema.nullable(),
  reason: z
    .enum(['served', 'no_eligible_campaign', 'bot_traffic', 'viewer_rate_limited'])
    .describe('why ad is null (or "served")'),
});
export type ServeResponse = z.infer<typeof ServeResponseSchema>;

export const ImpressionIdParamsSchema = z.object({ impressionId: UlidSchema });

export const CampaignReportQuerySchema = z.object({
  from: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  to: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
});
const SeriesSchema = z.array(z.object({ day: z.string(), value: z.number() }));
export const CampaignReportSchema = z.object({
  campaignId: UlidSchema,
  from: z.string(),
  to: z.string(),
  totals: z.object({
    impressions: z.number().int(),
    clicks: z.number().int(),
    spendCents: z.number().int(),
    conversions: z.number().int(),
    attributedValueCents: z.number().int(),
    ctr: z.number().nullable(),
    cpcCents: z.number().nullable(),
    cpmCents: z.number().nullable(),
    cpaCents: z.number().nullable(),
    roas: z.number().nullable(),
  }),
  series: z.object({ impressions: SeriesSchema, clicks: SeriesSchema, spendCents: SeriesSchema }),
});
export type CampaignReport = z.infer<typeof CampaignReportSchema>;
